import { useMemo } from "react";

import { canAccessFeature, featureForRoutePath } from "../../auth/featureScopes";
import { useAuth } from "../../contexts/AuthContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import { HelpArticle, helpArticles } from "./helpArticles";

/**
 * Help articles the current user should be able to read.
 *
 * Deliberately mirrors withFeatureScopeGate in DashboardLayout so that help
 * visibility tracks the sidebar: if you can't open the page, you don't get the
 * guide for it. Three gates, all reusing existing primitives:
 *
 *  - adminOnly       -> the isAdmin role claim
 *  - featureFlag     -> the customer's feature list (presence-based, as the sidebar does)
 *  - route           -> featureForRoutePath + canAccessFeature for the role's scopes
 */
export default function useVisibleHelpArticles(): {
  articles: HelpArticle[];
  isLoading: boolean;
} {
  const { claims, featureScopes } = useAuth();
  const { features } = useCustomerDetails();

  const flags = useMemo(
    () => features.data?.data.map((feature) => feature.name),
    [features.data]
  );

  const articles = useMemo(() => {
    if (!flags || !claims) return [];
    const isTme = flags.includes("TME");

    return helpArticles.filter((article) => {
      if (article.adminOnly && !claims.isAdmin) return false;
      if (article.featureFlag && !flags.includes(article.featureFlag)) return false;

      if (article.route) {
        const feature = featureForRoutePath(article.route, isTme);
        if (feature && !canAccessFeature(featureScopes, feature)) return false;
      }

      return true;
    });
  }, [claims, featureScopes, flags]);

  return { articles, isLoading: features.isLoading || !flags || !claims };
}

/** Whether a single article is readable by the current user. */
export function useCanReadHelpArticle(slug: string): {
  allowed: boolean;
  isLoading: boolean;
} {
  const { articles, isLoading } = useVisibleHelpArticles();

  return {
    allowed: articles.some((article) => article.slug === slug),
    isLoading,
  };
}
